import { RefObject, useEffect } from "react";

const SCROLL_OFFSET = 100;
const FLASH_CLASS = "footnote-flash";
const FLASH_DURATION = 1400;

// The page scrolls inside #scroll-container rather than the window, and the
// router owns the URL, so footnote jumps are handled here instead of letting
// the browser follow the hash.
export function scrollToId(id: string): HTMLElement | null {
  const element = document.getElementById(id);
  const scrollContainer = document.getElementById("scroll-container");
  if (!element || !scrollContainer) return null;

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  scrollContainer.scrollTo({
    top: scrollContainer.scrollTop + elementRect.top - containerRect.top - SCROLL_OFFSET,
    behavior: "smooth",
  });

  return element;
}

function flash(element: HTMLElement) {
  element.classList.remove(FLASH_CLASS);
  // Force a reflow so the animation restarts on repeat clicks.
  void element.offsetWidth;
  element.classList.add(FLASH_CLASS);
  window.setTimeout(() => element.classList.remove(FLASH_CLASS), FLASH_DURATION);
}

/**
 * Makes footnote markers scroll to their entry and the entry's key scroll back
 * to the first citation, briefly highlighting wherever you land.
 */
export function useFootnoteNavigation(
  contentRef: RefObject<HTMLElement | null>,
  htmlContent: string,
) {
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
        "a[data-footnote-ref], a[data-footnote-back]",
      );
      if (!link || !container.contains(link)) return;

      const targetId = link.getAttribute("href")?.replace(/^#/, "");
      if (!targetId) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();
      scrollToId(targetId);
      flash(target);
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [contentRef, htmlContent]);
}
